import { all, call, put, take, takeEvery } from 'redux-saga/effects'
import { t } from 'ttag'
import { getWhoAmI, getWhoAmIFail, getWhoAmISuccess } from '../../../common/auth/authActions'
import { addNotification, clearNotification } from '../../../common/notifications/NotificationActions'
import { INotification } from '../../../common/notifications/NotificationReducers'
import api from '../../../services/api.services'
import { doSignUp, doSignUpFail, doSignUpSuccess } from './SignUpActions'
import { push } from 'connected-react-router'
import { getErrorMsg } from '../../../common/helpers/translations'
import { SavePassword } from 'capacitor-ios-autofill-save-password'
import { Capacitor } from '@capacitor/core'
import { scrollToTop } from '../../../common/helpers/scroll'
import { ROUTES } from '../../../common/Routes/routePaths'
import { setVerifyEmailContext } from '../../VerifyEmail/store/VerifyEmailActions'

function* sagaSignUp({ payload }: any) {
  try {
    const { companyCode, email, password, lang } = payload
    yield put({ type: String(clearNotification) })
    const response: any = yield call(api.auth.signUp, { companyCode, email, password, lang })

    // If the company has email verification enabled, sync auth state (so the router respects
    // the mid-verification session) and then redirect to the verification screen. Purpose is
    // stored in Redux (not in router state) so concurrent reLocate() redirects that push to
    // /verify-email without state don't clobber it.
    if (response && response.requiresEmailVerification) {
      yield put(setVerifyEmailContext({ purpose: 'SIGNUP', email }))
      yield put({ type: String(getWhoAmI) })
      yield take([String(getWhoAmISuccess), String(getWhoAmIFail)])
      yield put({ type: String(doSignUpSuccess) })
      yield put(push(ROUTES.VERIFY_EMAIL.to))
      return
    }

    // Ensure that you're logged in now.
    yield put({ type: String(getWhoAmI) })
    // Wait for getWhoAmI to finish.
    yield take([String(getWhoAmISuccess), String(getWhoAmIFail)])
    yield put({ type: String(doSignUpSuccess) })

    if (Capacitor.getPlatform() === 'ios' && email && password) {
      yield SavePassword.promptDialog({
        username: email,
        password: password,
      })
    }

    yield put(push(ROUTES.REGISTER_USER.to))
  } catch (e: any) {
    yield put({ type: String(doSignUpFail) })
    // Handle ssoRequired: backend blocks signup when company requires SSO
    if (e.message && e.message === 'ssoRequired') {
      const ssoNotification: INotification = {
        type: 'warning',
        content: t`This organization requires SSO login. Please use the login page to sign in with SSO.`,
      }
      yield put({ type: String(addNotification), payload: ssoNotification })
    } else if (e.message && e.message === 'invalidCompanyCode') {
      const signupErrorNotification: INotification = {
        type: 'warning',
        content: t`Sorry, the sign-up code provided is not valid`,
      }
      yield put({ type: String(addNotification), payload: signupErrorNotification })
    } else if (e.message && e.message === 'emailExists') {
      const signupErrorNotification: INotification = {
        type: 'warning',
        content: t`A user with the same email address exists`,
      }
      yield put({ type: String(addNotification), payload: signupErrorNotification })
    } else if (e.message && e.message === 'disallowedDomain') {
      const signupErrorNotification: INotification = {
        type: 'warning',
        content: t`Sorry, the sign-up code provided does not allow signing up with the specified email address. Please sign up using another email address or contact your employer.`,
      }
      yield put({ type: String(addNotification), payload: signupErrorNotification })
    } else if (e.message && e.message === 'domainNotVerified') {
      // Domain verification required: company's email domain is not verified for SSO
      const notification: INotification = {
        type: 'warning',
        content: t`Your organization's domain has not been verified. Please contact your administrator.`,
      }
      yield put({ type: String(addNotification), payload: notification })
    } else if (e.message && e.message === 'disallowedRegion') {
      const signupErrorNotification: INotification = {
        type: 'warning',
        content: t`Sorry, the given sign-up code does not allow signing up from your region.`,
      }
      yield put({ type: String(addNotification), payload: signupErrorNotification })
    } else if (e.message && e.message === 'userLimitExceeded') {
      const signupErrorNotification: INotification = {
        type: 'warning',
        content: t`Sorry, this community has exceeded its user limit. Please contact your challenge organizer for more info.`,
      }
      yield put({ type: String(addNotification), payload: signupErrorNotification })
    } else {
      const signupErrorNotification: INotification = {
        type: 'warning',
        content: getErrorMsg(
          e.message && e.message !== 'No message has been provided by the server' ? e.message : 'sign up',
        ),
      }
      yield put({ type: String(addNotification), payload: signupErrorNotification })
    }
    scrollToTop()
  }
}

export function* signUpSagas() {
  yield all([takeEvery(String(doSignUp), sagaSignUp)])
}
